// src/shared/api/interceptors/tokenGetter.ts
type TokenGetterOptions = {
  cacheMode?: 'on' | 'off' | 'cache-only';
  authorizationParams?: {
    audience?: string;
    scope?: string;
    [key: string]: string | undefined;
  };
};

type TokenGetter = (options?: TokenGetterOptions) => Promise<string>;

let _getter: TokenGetter | null = null;
let _notifyReady: ((getter: TokenGetter) => void) | null = null;
let _ready: Promise<TokenGetter> | null = null;

/** How long a request will wait for Auth0's getter before going out bare. */
export const TOKEN_GETTER_WAIT_MS = 5000;

export const setTokenGetter = (getter: TokenGetter): void => {
  _getter = getter;
  _notifyReady?.(getter);
  _notifyReady = null;
};

export const getTokenGetter = (): TokenGetter | null => _getter;

/**
 * Resolve with the token getter, waiting briefly if it has not been registered
 * yet; resolve `null` if it never arrives.
 *
 * Why this exists: the getter is registered by a React component, but requests
 * are started by React Query the moment a query is enabled. Whichever component
 * calls useMyProfile() first wins the race, and that is decided by position in
 * the provider tree — CurrencyProvider and PolicyAcceptanceProvider both sit
 * ABOVE the layout that used to register it. When the getter was missing, the
 * auth interceptor simply skipped the Authorization header and the request
 * 401'd, leaving the retry interceptor to refresh and replay it. Every cold
 * load therefore made a doomed request first.
 *
 * Waiting makes correctness independent of render order, so adding a provider
 * above the tree cannot reintroduce it.
 */
export function whenTokenGetterReady(
  timeoutMs: number = TOKEN_GETTER_WAIT_MS,
): Promise<TokenGetter | null> {
  if (_getter) return Promise.resolve(_getter);

  if (!_ready) {
    _ready = new Promise<TokenGetter>((resolve) => {
      _notifyReady = resolve;
    });
  }

  // Never hang: a logged-out session has no getter coming, and the request
  // should be allowed to fail normally rather than stall the UI.
  return Promise.race([
    _ready,
    new Promise<null>((resolve) => setTimeout(() => resolve(null), timeoutMs)),
  ]);
}

/** Test-only: clear the registered getter and any pending waiters. */
export function __resetTokenGetter(): void {
  _getter = null;
  _notifyReady = null;
  _ready = null;
}
